import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users2, Plus, Save, X, Trash2, Info, RefreshCw, Sparkles, Settings2,
  UserPlus, Shield, ChevronRight,
} from "lucide-react";

/**
 * Groups admin page (v29).
 * Named cohorts of users. Two flavours:
 *   - static: admin adds members manually via /users bulk actions.
 *   - auto:   filter-driven, membership recomputed by the engine.
 * Cadences can target a group via Cadence.group_id.
 * The seeded "Deleted Users" group is system=true and cannot be deleted.
 */

const BLANK_FILTER = {
  subscription_status: null,
  city: "",
  state: "",
  country: "",
  exam: null,
  tags_any: [],
  tags_all: [],
  days_since_join_min: "",
  days_since_join_max: "",
  cps_min: "",
  cps_max: "",
  last_login_older_than_days: "",
  dnd: null,
};

const BLANK_GROUP = {
  name: "",
  description: "",
  auto: false,
  color: "slate",
  filter: { ...BLANK_FILTER },
};

const COLORS = ["slate", "red", "orange", "amber", "emerald", "teal", "sky", "indigo", "violet", "pink"];
const STATUSES = ["trial", "paid", "expired", "cancelled", "deleted"];

// Numeric fields on GroupFilter — the BLANK_FILTER uses "" for the UI which
// FastAPI rejects (422). Coerce these to null before hitting the API.
const FILTER_NUM_FIELDS = [
  "days_since_join_min", "days_since_join_max",
  "cps_min", "cps_max",
  "last_login_older_than_days",
];
const FILTER_STR_FIELDS = ["city", "state", "country"];
const FILTER_ARR_FIELDS = ["tags_any", "tags_all", "subscription_status", "exam"];

/** Coerce UI-blank values into API-safe shape (null/omit vs empty string). */
function sanitizeFilter(f) {
  if (!f) return null;
  const out = { ...f };
  for (const k of FILTER_NUM_FIELDS) {
    if (out[k] === "" || out[k] === undefined || Number.isNaN(out[k])) out[k] = null;
  }
  for (const k of FILTER_STR_FIELDS) {
    if (out[k] === undefined) out[k] = "";
  }
  for (const k of FILTER_ARR_FIELDS) {
    if (!Array.isArray(out[k]) || out[k].length === 0) out[k] = null;
  }
  return out;
}

/** Turn any backend error object into a plain string safe for toast/React. */
function errText(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => (x?.msg ? `${(x.loc || []).join(".")}: ${x.msg}` : JSON.stringify(x))).join(" · ");
  }
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  if (typeof e?.message === "string") return e.message;
  return fallback;
}

function GroupChip({ g, active, onClick }) {
  const dot = `bg-${g.color || "slate"}-500`;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-md border transition-colors duration-150 ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400"
      }`}
      data-testid={`group-chip-${g.id}`}
    >
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dot}`}></span>
        <span className="font-semibold text-sm truncate">{g.name}</span>
        {g.system && <Shield className="h-3 w-3 opacity-70" title="System group — cannot be deleted" />}
      </div>
      <div className={`text-[10px] uppercase tracking-widest mt-1 ${active ? "text-slate-300" : "text-slate-500"}`}>
        {g.auto ? "Auto" : "Static"} · {g.member_count ?? 0} members
      </div>
    </button>
  );
}

// -- Filter builder ---------------------------------------------------------
function FilterBuilder({ filter, onChange, onCountRefresh, count }) {
  const set = (patch) => onChange({ ...filter, ...patch });
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-md p-3">
        <div className="text-xs text-indigo-900 leading-relaxed">
          <b>Live count:</b> <span className="font-mono">{count ?? "—"}</span> user(s) currently match this filter.
        </div>
        <button
          type="button"
          onClick={onCountRefresh}
          className="text-[10px] font-semibold uppercase tracking-widest text-indigo-700 hover:text-indigo-900 flex items-center gap-1"
          data-testid="filter-preview-refresh"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Subscription status (any of)</span>
          <div className="mt-1 flex flex-wrap gap-1">
            {STATUSES.map((s) => {
              const on = (filter.subscription_status || []).includes(s);
              return (
                <button
                  key={s} type="button"
                  onClick={() => {
                    const cur = filter.subscription_status || [];
                    set({ subscription_status: on ? cur.filter((x) => x !== s) : [...cur, s] });
                  }}
                  className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${
                    on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"
                  }`}
                >{s}</button>
              );
            })}
          </div>
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Exam (comma sep)</span>
          <input
            value={(filter.exam || []).join(", ")}
            onChange={(e) => set({ exam: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) || null })}
            placeholder="NCLEX, DHA"
            className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">City</span>
          <input value={filter.city || ""} onChange={(e) => set({ city: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">State</span>
          <input value={filter.state || ""} onChange={(e) => set({ state: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
        </label>
        <label className="block col-span-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Country</span>
          <input value={filter.country || ""} onChange={(e) => set({ country: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Has ANY tag (comma sep)</span>
          <input
            value={(filter.tags_any || []).join(", ")}
            onChange={(e) => set({ tags_any: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            placeholder="hot_lead, engaged"
            className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Has ALL tags (comma sep)</span>
          <input
            value={(filter.tags_all || []).join(", ")}
            onChange={(e) => set({ tags_all: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            placeholder="import_batch_x, mumbai"
            className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Days since join · MIN</span>
          <input type="number" value={filter.days_since_join_min ?? ""} onChange={(e) => set({ days_since_join_min: e.target.value === "" ? null : parseInt(e.target.value, 10) })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Days since join · MAX</span>
          <input type="number" value={filter.days_since_join_max ?? ""} onChange={(e) => set({ days_since_join_max: e.target.value === "" ? null : parseInt(e.target.value, 10) })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">CPS score MIN</span>
          <input type="number" min="0" max="100" value={filter.cps_min ?? ""} onChange={(e) => set({ cps_min: e.target.value === "" ? null : parseInt(e.target.value, 10) })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">CPS score MAX</span>
          <input type="number" min="0" max="100" value={filter.cps_max ?? ""} onChange={(e) => set({ cps_max: e.target.value === "" ? null : parseInt(e.target.value, 10) })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
        </label>
        <label className="block col-span-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Last login older than (days)</span>
          <input type="number" value={filter.last_login_older_than_days ?? ""} onChange={(e) => set({ last_login_older_than_days: e.target.value === "" ? null : parseInt(e.target.value, 10) })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
        </label>
      </div>
    </div>
  );
}

// -- Members panel ----------------------------------------------------------
function MembersPanel({ groupId, group, refreshKey, onChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [removing, setRemoving] = useState(false);
  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/groups/${groupId}/members`);
      setMembers(data.members || []);
      setSelectedIds(new Set());
    } finally { setLoading(false); }
  }, [groupId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const isAuto = !!group?.auto;

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelectedIds((prev) => (
      prev.size === members.length ? new Set() : new Set(members.map((m) => m.id))
    ));
  };
  const removeSelected = async () => {
    if (isAuto) return toast.error("Auto groups can't be edited manually");
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Remove ${selectedIds.size} member(s) from this group?`)) return;
    setRemoving(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/members/remove`, {
        user_ids: Array.from(selectedIds),
      });
      toast.success(`Removed ${data.removed} member(s)`);
      onChange?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setRemoving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Current members ({members.length})
          {isAuto && (
            <span className="ml-2 text-[9px] normal-case tracking-normal text-blue-600 font-medium">
              · rule-driven, read-only
            </span>
          )}
        </div>
        {!isAuto && selectedIds.size > 0 && (
          <button
            data-testid="group-members-remove-selected"
            onClick={removeSelected}
            disabled={removing}
            className="text-[11px] font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-3 py-1 rounded-md disabled:opacity-70"
          >
            {removing ? "Removing…" : `Remove ${selectedIds.size} selected`}
          </button>
        )}
      </div>
      {loading ? (
        <div className="text-slate-500 text-xs">Loading…</div>
      ) : members.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-6 text-center text-slate-500 text-sm">
          No members yet.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md max-h-80 overflow-y-auto">
          {!isAuto && (
            <label className="border-b border-slate-100 px-3 py-2 flex items-center gap-3 text-[11px] font-semibold text-slate-500 uppercase tracking-widest sticky top-0 bg-white z-10">
              <input
                type="checkbox"
                data-testid="group-members-select-all"
                checked={selectedIds.size > 0 && selectedIds.size === members.length}
                onChange={toggleAll}
                className="h-3.5 w-3.5"
              />
              Select all
            </label>
          )}
          {members.map((m) => {
            const alsoInOthers = (m.also_in_groups || []).filter((g) => g.id !== groupId);
            const showOverlapAlert = isAuto && alsoInOthers.length > 0;
            return (
              <div
                key={m.id}
                data-testid={`group-member-row-${m.id}`}
                className={`border-b border-slate-100 last:border-0 px-3 py-2 flex items-center gap-3 text-sm ${
                  showOverlapAlert ? "bg-red-50/60" : ""
                }`}
              >
                {!isAuto && (
                  <input
                    type="checkbox"
                    data-testid={`group-member-select-${m.id}`}
                    checked={selectedIds.has(m.id)}
                    onChange={() => toggleOne(m.id)}
                    className="h-3.5 w-3.5 shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {m.name || "—"}
                    {showOverlapAlert && (
                      <span
                        data-testid={`group-member-overlap-${m.id}`}
                        title={"Also in: " + alsoInOthers.map((g) => g.name).join(", ")}
                        className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-600 text-white"
                      >
                        Overlap
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {m.email} · {m.phone}
                    {alsoInOthers.length > 0 && !isAuto && (
                      <span className="ml-2 text-slate-500">
                        · Also in: {alsoInOthers.map((g) => g.name).join(", ")}
                      </span>
                    )}
                    {showOverlapAlert && (
                      <span className="ml-2 text-red-700">
                        · Also in: {alsoInOthers.map((g) => g.name).join(", ")}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-slate-100 text-slate-700 shrink-0">
                  {m.subscription_status}
                </span>
                {m.dnd && (
                  <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-600 text-white">DND</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -- Page -------------------------------------------------------------------
export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [editing, setEditing] = useState(null); // group in edit mode (or blank for new)
  const [previewCount, setPreviewCount] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/groups");
      setGroups(data);
      if (!activeId && data.length > 0) setActiveId(data[0].id);
    } finally { setLoading(false); }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => groups.find((g) => g.id === activeId) || null, [groups, activeId]);

  const startNew = () => { setEditing({ ...BLANK_GROUP }); setPreviewCount(null); };
  const startEdit = (g) => {
    setEditing({
      ...BLANK_GROUP, ...g,
      filter: { ...BLANK_FILTER, ...(g.filter || {}) },
    });
    setPreviewCount(g.member_count ?? null);
  };

  const refreshPreview = useCallback(async () => {
    if (!editing?.auto) { setPreviewCount(null); return; }
    try {
      const { data } = await api.post("/groups/preview", { filter: sanitizeFilter(editing.filter) });
      setPreviewCount(data.count);
    } catch { setPreviewCount(null); }
  }, [editing]);

  useEffect(() => {
    if (editing?.auto) refreshPreview();
  }, [editing?.auto, editing?.filter, refreshPreview]);

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Group name is required");
    const payload = {
      name: editing.name.trim(),
      description: editing.description || "",
      auto: !!editing.auto,
      filter: editing.auto ? sanitizeFilter(editing.filter) : null,
      color: editing.color || "slate",
    };
    try {
      if (editing.id) {
        await api.patch(`/groups/${editing.id}`, payload);
        toast.success("Group updated");
      } else {
        const { data } = await api.post("/groups", payload);
        toast.success("Group created");
        setActiveId(data.id);
      }
      setEditing(null);
      await load();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(errText(e, "Save failed"));
    }
  };

  const doDelete = async () => {
    if (!active) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      setTimeout(() => setConfirmingDelete(false), 5000);
      return;
    }
    setConfirmingDelete(false);
    try {
      await api.delete(`/groups/${active.id}`);
      toast.success(`Deleted "${active.name}"`);
      setActiveId(null);
      await load();
    } catch (e) {
      toast.error(errText(e, "Delete failed"));
    }
  };

  const forceRecompute = async () => {
    if (!active) return;
    try {
      const { data } = await api.post(`/groups/${active.id}/recompute`);
      toast.success(`Recomputed — ${data.size} member(s), +${data.added} / -${data.removed}`);
      setRefreshKey((k) => k + 1);
      await load();
    } catch (e) {
      toast.error(errText(e, "Recompute failed"));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="groups-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Segmentation</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <Users2 className="h-6 w-6 text-indigo-500" /> Groups
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Named cohorts of users. Attach one to any cadence step to target only that group.
            Use <b>Auto</b> to define membership by rule (self-updating), or leave it <b>Static</b> to hand-pick users.
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="group-add-btn"
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-4 py-2 rounded-md"
        >
          <Plus className="h-4 w-4" /> New group
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left rail — group list */}
        <aside className="col-span-3 space-y-2">
          {loading ? <div className="text-slate-500 text-xs">Loading…</div> :
            groups.map((g) => (
              <GroupChip key={g.id} g={g} active={activeId === g.id} onClick={() => { setActiveId(g.id); setEditing(null); }} />
            ))
          }
        </aside>

        {/* Right — detail / editor */}
        <main className="col-span-9">
          {editing ? (
            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="group-editor">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-extrabold">{editing.id ? "Edit group" : "New group"}</h2>
                <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-800"><X className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <label className="block col-span-2">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Name</span>
                  <input
                    data-testid="group-form-name"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2"
                  />
                </label>
                <label className="block col-span-2">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Description</span>
                  <input
                    value={editing.description}
                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2"
                  />
                </label>
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 block mb-1">Colour</span>
                  <div className="flex flex-wrap gap-1">
                    {COLORS.map((c) => (
                      <button
                        key={c} type="button"
                        onClick={() => setEditing({ ...editing, color: c })}
                        className={`h-6 w-6 rounded-full border-2 bg-${c}-500 ${editing.color === c ? "border-slate-900" : "border-transparent"}`}
                        title={c}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                <label className="flex items-center justify-between mb-3 cursor-pointer">
                  <div>
                    <div className="text-sm font-bold">Auto-populate this group</div>
                    <div className="text-xs text-slate-500">When ON, Connect keeps membership in sync with the rules below.</div>
                  </div>
                  <input
                    data-testid="group-form-auto"
                    type="checkbox"
                    checked={!!editing.auto}
                    onChange={(e) => setEditing({ ...editing, auto: e.target.checked })}
                    disabled={editing.system}
                    className="h-5 w-5 accent-indigo-600"
                  />
                </label>
                {editing.auto ? (
                  <FilterBuilder
                    filter={editing.filter}
                    onChange={(f) => setEditing({ ...editing, filter: f })}
                    count={previewCount}
                    onCountRefresh={refreshPreview}
                  />
                ) : (
                  <div className="text-xs text-slate-500 italic">
                    Static group — you&apos;ll add users from the Users page (multi-select + &quot;Add to group&quot;).
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setEditing(null)} className="text-xs font-semibold text-slate-600 px-3 py-2">Cancel</button>
                <button
                  onClick={save}
                  data-testid="group-form-save"
                  className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-4 py-2 rounded-md"
                ><Save className="h-3.5 w-3.5" /> Save</button>
              </div>
            </div>
          ) : active ? (
            <div className="space-y-5">
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`h-2.5 w-2.5 rounded-full bg-${active.color || "slate"}-500`}></span>
                      <h2 className="font-heading text-xl font-extrabold">{active.name}</h2>
                      {active.system && (
                        <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-800 text-white">System</span>
                      )}
                      <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${
                        active.auto ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-50 text-slate-600 border-slate-200"
                      }`}>{active.auto ? "Auto" : "Static"}</span>
                    </div>
                    {active.description && <p className="text-sm text-slate-600 mt-1">{active.description}</p>}
                    <div className="text-[11px] text-slate-500 mt-2">
                      {active.member_count ?? 0} member(s)
                      {active.last_recomputed_at && (
                        <> · last recomputed {new Date(active.last_recomputed_at).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {active.auto && (
                      <button
                        onClick={forceRecompute}
                        data-testid="group-recompute-btn"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 border border-indigo-300 hover:bg-indigo-50 px-3 py-1.5 rounded-md"
                      ><RefreshCw className="h-3 w-3" /> Recompute now</button>
                    )}
                    <button
                      onClick={() => startEdit(active)}
                      data-testid="group-edit-btn"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 border border-slate-300 hover:bg-slate-50 px-3 py-1.5 rounded-md"
                    ><Settings2 className="h-3 w-3" /> Edit</button>
                    {!active.system && (
                      <button
                        onClick={doDelete}
                        data-testid="group-delete-btn"
                        className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
                          confirmingDelete
                            ? "bg-red-600 text-white hover:bg-red-500"
                            : "text-red-600 border border-red-300 hover:bg-red-50"
                        }`}
                      ><Trash2 className="h-3 w-3" /> {confirmingDelete ? "Click to confirm" : "Delete"}</button>
                    )}
                  </div>
                </div>
                {active.system && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded p-3 flex items-start gap-2 text-xs text-red-900">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <div>
                      This is a system group — Connect keeps it in sync automatically. You can view / edit the filter, but it can&apos;t be deleted.
                    </div>
                  </div>
                )}
              </div>
              <MembersPanel
                groupId={active.id}
                group={active}
                refreshKey={refreshKey}
                onChange={() => setRefreshKey((k) => k + 1)}
              />
            </div>
          ) : (
            <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500">
              Pick a group on the left, or click <b>New group</b>.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
